import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { hierarchy, partition, HierarchyRectangularNode } from 'd3-hierarchy';
import { Group } from '@visx/group';
import { Arc } from '@visx/shape';
import { scaleOrdinal } from '@visx/scale';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { colors, lightenColor } from './Sunburst/utilsSunburst';
import Tooltip from './Sunburst/Tooltype';
import { EmptyState } from '../../../Components/EmptyState/EmptyState';
import Portal from '../../../Components/Portal';
import ArcSelectionFunnel from './Sunburst/ArcSelectionFunnel';
import styles from './Sunburst.module.css';

interface TreeNode {
	name: string;
	value: number;
	children?: TreeNode[];
	sessionCount?: number;
}

type HierarchyNodeWithData = HierarchyRectangularNode<TreeNode>;


function addLossArcsToAllLevels(node: TreeNode): TreeNode {
	if (!node.children || node.children.length === 0) return node;
	node.children = node.children.map(child => addLossArcsToAllLevels(child));
	const childrenSum = node.children.reduce((sum: number, child: TreeNode) => {
		return sum + child.value;
	}, 0);

	const childrenSessionSum = node.children.reduce((sum: number, child: TreeNode) => {
		return sum + (child.sessionCount || child.value);
	}, 0);

	const difference = node.value - childrenSum;
	const sessionDifference = (node.sessionCount || node.value) - childrenSessionSum;
	
	if (difference > 0) {
		const existingLossIndex = node.children.findIndex(child => child.name === 'LostSession');
		
		if (existingLossIndex >= 0) {
			node.children[existingLossIndex].value += difference;
			node.children[existingLossIndex].sessionCount = (node.children[existingLossIndex].sessionCount || 0) + sessionDifference;
		} else {
			node.children.push({
				name: 'LostSession',
				value: difference,
				sessionCount: sessionDifference > 0 ? sessionDifference : difference
			});
		}
	}
	
	return node;
}

interface SunBurstComponentProps {
	transformedData: TreeNode;
	dataIsEmpty: boolean;
	orderBy?: string;
}

const SunBurstComponent: React.FC<SunBurstComponentProps> = ({
	transformedData,
	dataIsEmpty,
	orderBy = ''
}) => {

	const width = 500;
	const height = width;
	const [hoveredNode, setHoveredNode] = useState<HierarchyNodeWithData | null>(null);
	const [hoveredNodeAncestors, setHoveredNodeAncestors] = useState<HierarchyNodeWithData[]>([]);
	
	const [selectedJourneyData, setselectedJourneyData] = useState<TreeNode | null>(null);
	const [activeTab, setActiveTab] = useState<string>('urls');
	const [selectedAncestors, setSelectedAncestors] = useState<HierarchyNodeWithData[]>([]);
	const [totalLevel1Sessions, settotalLevel1Sessions] = useState(0);

	const dataWithDropoff = useMemo(() => {
		const clonedData = JSON.parse(JSON.stringify(transformedData)) as TreeNode;
		return addLossArcsToAllLevels(clonedData);
	}, [transformedData]);

	const root = useMemo(() => {
		function sortAllLevels(a, b) {
			if (orderBy === 'SessionCount') {
				const aSessionCount = a.data.sessionCount ?? a.value ?? 0;
				const bSessionCount = b.data.sessionCount ?? b.value ?? 0;
				return bSessionCount - aSessionCount;
			} else {
				if (a.data.name === 'LostSession') return 1;
				if (b.data.name === 'LostSession') return -1;
				return 0;
			}
		}

		const hierarchyRoot = hierarchy(dataWithDropoff)
			.sum(d => d.value)
			.sort(sortAllLevels);

		function sortChildrenRecursively(node) {
			if (node.children) {
				node.children.sort(sortAllLevels);
				node.children.forEach(sortChildrenRecursively);
			}
		}

		sortChildrenRecursively(hierarchyRoot);

		function normalizeHierarchy(node) {
			if (node.children) {
				node.children.forEach(normalizeHierarchy);
				const childrenSum = node.children.reduce((sum, child) => sum + (child.value || 0), 0);
				if (Math.abs(node.value - childrenSum) > 0.001) {
					node.value = childrenSum;
				}
			}
		}

		normalizeHierarchy(hierarchyRoot);
		return hierarchyRoot;
	}, [dataWithDropoff, orderBy]);

	const partitionLayout = useMemo(() => partition().size([2 * Math.PI, Math.min(width, height) / 2]), [width, height]);
	const rootNode = useMemo(() => partitionLayout(root), [partitionLayout, root]);

	const arcsData = useMemo(() => {
		return rootNode.descendants().slice(1) as HierarchyNodeWithData[];
	}, [rootNode]);

	const colorDomain = useMemo(() => {
		if (dataWithDropoff.children) {
			//const names = dataWithDropoff.children.map(child => child.name).filter(n => n !== 'LostSession').sort();
			const names = dataWithDropoff.children.map(child => child.name).filter(n => n !== 'LostSession');
			if (dataWithDropoff.children.some(child => child.name === 'LostSession')) {
				names.push('LostSession');
			}
			return [dataWithDropoff.name, ...names];
		}
		return [dataWithDropoff.name];
	}, [dataWithDropoff]);

	const colorScale = useMemo(() => scaleOrdinal({
		domain: colorDomain,
		range: [...colors, '#AAA'],
	}), [colorDomain]);

	const getInitialLegend = useCallback(() => arcsData.filter(node => node.depth === 1).map(node => {
		const data = node.data;
		return {
			name: data.name === 'LostSession' ? 'Site exit' : data.name,
			color: colorScale(data.name),
			value: node.value,
			data: {
				...data,
				sessionCount: typeof data.sessionCount === 'number' ? data.sessionCount : node.value 
			},
			depth: node.depth,
			node,
			lossFromParent: node.parent && node.parent.data
				? (((node.parent.data.sessionCount ?? node.parent.value) || 0) - ((data.sessionCount ?? node.value) || 0))
				: 0
		};
	}), [arcsData, colorScale]);

	const [legendData] = useState(getInitialLegend);
	const [tunnelData, setTunnelData]:any[] = useState([]);

	const { showTooltip, hideTooltip, tooltipOpen, tooltipData, tooltipLeft = 0, tooltipTop = 0 } = useTooltip<TreeNode>();

	const findNode = (nodeOrItem: HierarchyNodeWithData | any) => {
		return arcsData.find(
			node => node.data.name === nodeOrItem.name && node.depth === nodeOrItem.depth
		);
	};

	const handleMouseMove = useCallback((event: React.MouseEvent, node: HierarchyNodeWithData) => {
		const { clientX, clientY } = event;

		if (node.ancestors) {
			const ancestors = node.ancestors();
			setHoveredNodeAncestors(ancestors);
		}

		setHoveredNode(node);
		showTooltip({
			tooltipLeft: clientX + 10,
			tooltipTop: clientY + 10,
			tooltipData: node.data,
		});
	}, [showTooltip]);

	const handleMouseLeave = useCallback(() => {
		hideTooltip();
		setHoveredNode(null);
		setHoveredNodeAncestors([]);
	}, [hideTooltip]);

	const handleArcClick = useCallback((nodeOrItem: HierarchyNodeWithData | any) => {

		if (nodeOrItem.node) {
			handleArcClick(nodeOrItem.node);
			return;
		}
		if (nodeOrItem.ancestors) {
			setselectedJourneyData(nodeOrItem.data);
			const ancestors = nodeOrItem.ancestors();
			setSelectedAncestors(ancestors);
			const tunnel = [...ancestors.slice(1), nodeOrItem];
			setTunnelData(tunnel.map((desc, idx, arr) => {
				const data = desc.data;
				const parent = idx > 0 ? arr[idx - 1] : null;
				const parentSessionCount = parent ? (parent.data.sessionCount ?? parent.value) : null;
				const nodeSessionCount = data.sessionCount ?? desc.value;
				return {
					name: data.name,
					color: colorScale(data.name),
					value: desc.value,
					data: {
						...data,
						sessionCount: typeof data.sessionCount === 'number' ? data.sessionCount : desc.value
					},
					depth: desc.depth,
					node: desc,
					lossFromParent: parentSessionCount !== null ? parentSessionCount - nodeSessionCount : 0
				};
			}));
		} else {
			const foundNode = findNode(nodeOrItem);
			if (foundNode) {
				handleArcClick(foundNode);
			}
		}
	}, [colorScale, arcsData, legendData]);

	const handleDeselect = useCallback(() => {
		setSelectedAncestors([]);
		//setLegendData(getInitialLegend());
		setTunnelData([]);
		setselectedJourneyData(null);
	}, [getInitialLegend]);

	useEffect(() => {
		const level1Nodes = rootNode
			.descendants()
			.filter(node => node.depth === 1);

		const totalSessions = level1Nodes.reduce((sum, node) => {
			const data = node.data as TreeNode;
			const sessionCount = data.sessionCount;
			if (typeof sessionCount === 'number' && !isNaN(sessionCount)) {
				return sum + sessionCount;
			}
			return sum;
		}, 0);

		settotalLevel1Sessions(totalSessions);
	}, [rootNode]);

	if (transformedData === null) {
		return null;
	}

	if (dataIsEmpty) {
		return <EmptyState
			icon="fas fa-search"
			verticalSize={'xl'}
			textSize={'l'}
			title={'No matching data'}
			text={'Please try another filters'}
		/>;
	}
	
	return (
		<div className='flex flex_align_stretch'>
			<div className={['flex_item_full', styles.main].join(' ')}>
				<div className={styles.main_title}>
					<div className='h3'>
						Journeys from landing pages
					</div>
				</div>
				<div className={styles.sunburst_container} style={{width: width, height: height}} onClick={handleDeselect}>
					<svg className={styles.sunburst_svg}>
						<Group top={height / 2} left={width / 2}>
							{arcsData.map((node, i) => {
								
								const globalOffset = 0;
								const innerRadius = node.y0 + globalOffset;
								const outerRadius = node.y1 + globalOffset;

								const arc = {
									innerRadius,
									outerRadius,
									startAngle: node.x0,
									endAngle: node.x1,
								};
								const isHighlighted = node === hoveredNode;
								const isInSelectedPath = selectedAncestors.some(ancestor => ancestor === node);
								const isInHoveredAncestors = hoveredNodeAncestors.some(ancestor => ancestor === node);
								const hasSelection = selectedAncestors.length > 0;
								const isSiteExit = node.data.name === 'LostSession';
								const color = isSiteExit ? '#CACCD8' : colorScale(node.data.name);
								const fillColor = isHighlighted ? lightenColor(color, 30) : color;

								let opacity = 1;
								if (hoveredNodeAncestors.length) {
									opacity = isInHoveredAncestors ? 1 : 0.3;
								} else {
									opacity = (isInSelectedPath || !hasSelection) ? 1 : 0.3;
								}
								return (
									<React.Fragment key={`arc-${i}`}>
										<Group key={`group-${i}`}>
											<Arc
												key={`arc-color-${i}`}
												{...arc}
												fill={fillColor}
												stroke="#fff"
												strokeWidth="1.5"
												opacity={opacity}
												style={{ cursor: 'pointer' }} 
												padAngle={0}
												onMouseMove={(event) => {
													handleMouseMove(event, node);
												}}
												onMouseLeave={handleMouseLeave}
												onClick={(e) => {
													e.stopPropagation();
													handleArcClick(node); // Toujours appeler handleArcClick
												}}
											/>
										</Group>
									</React.Fragment>
								);
							})}
						</Group>
					</svg>
					{tooltipOpen && tooltipData && hoveredNode && (
						<Portal>
							<Tooltip
								TooltipWithBounds={TooltipWithBounds}
								tooltipData={{
									...tooltipData,
									...(tooltipData.name === 'LostSession' ? { name: 'Site exit', sessionCount: tooltipData.value } : {})
								}}
								tooltipTop={tooltipTop}
								tooltipLeft={tooltipLeft}
								isSiteExit={tooltipData.name === 'LostSession'}
								totalLevel1Sessions={totalLevel1Sessions}
								parentNode={hoveredNode.parent ? hoveredNode.parent.data : null} 
							/>
						</Portal>
					)}
				</div>
				<div className={styles.sunburst_mention}>
					Click on an arch to see more details
				</div>
			</div>
			<div className={['flex_item_fix', selectedJourneyData ? styles.sidebar_l : styles.sidebar_m].join(' ')}>
				<ArcSelectionFunnel 
					selectedJourneyData={selectedJourneyData}
					setActiveTab={setActiveTab}
					activeTab={activeTab}
					legendData={legendData}
					tunnelData={tunnelData}
					totalLevel1Sessions={totalLevel1Sessions}
					handleDeselect={handleDeselect}
					handleArcClick={handleArcClick}
				/>
			</div>
		</div>
	);
};

export default SunBurstComponent;
